import type { SqlDataset, SqlMode, SqlObjectV2 } from "@bezpart-board/shared";
import { CUSTOM_SETUP_DEFAULT, datasetSetupSql } from "./sqlDatabases";
import type { SqlTableResult } from "./sqlRunner";
import { taskAt } from "./sqlTasks";

export const SQL_TEXT_MAX = 24_000;
export const SQL_SETUP_MAX = 40_000;
export const SQL_OUTPUT_MAX = 24_000;

export type SqlStatus = "idle" | "ok" | "fail";

export type SqlState = {
  dataset: SqlDataset;
  mode: SqlMode;
  setupSql: string;
  query: string;
  taskIndex: number;
  customPrompt: string;
  customSolution: string;
  status: SqlStatus;
  output: string;
  solvedIds: string[];
  showSchema: boolean;
};

export const SQL_QUERY_PLACEHOLDER = "-- Пишите запрос здесь\nSELECT * FROM ";

export const SQL_DEFAULTS: SqlState = {
  dataset: "airbnb",
  mode: "tasks",
  setupSql: CUSTOM_SETUP_DEFAULT,
  query: "SELECT * FROM Rooms",
  taskIndex: 0,
  customPrompt: "Условие задачи: что нужно вывести и в каком виде.",
  customSolution: "SELECT 1",
  status: "idle",
  output: "",
  solvedIds: [],
  showSchema: true,
};

export type SqlAction =
  | { type: "setDataset"; dataset: SqlDataset }
  | { type: "setMode"; mode: SqlMode }
  | { type: "setSetupSql"; setupSql: string }
  | { type: "setQuery"; query: string }
  | { type: "setTaskIndex"; taskIndex: number }
  | { type: "setCustomPrompt"; customPrompt: string }
  | { type: "setCustomSolution"; customSolution: string }
  | { type: "setResult"; status: SqlStatus; output: string; solvedId?: string }
  | { type: "unsolve"; taskId: string }
  | { type: "resetSolved" }
  | { type: "toggleSchema" };

function clampStr(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max);
}

export function readSqlState(obj: Partial<SqlObjectV2> | null | undefined): SqlState {
  const d = SQL_DEFAULTS;
  const dataset: SqlDataset =
    obj?.dataset === "airbnb" ||
    obj?.dataset === "airport" ||
    obj?.dataset === "family" ||
    obj?.dataset === "school" ||
    obj?.dataset === "custom"
      ? obj.dataset
      : d.dataset;
  const mode: SqlMode =
    obj?.mode === "tasks" || obj?.mode === "custom" || obj?.mode === "free" ? obj.mode : d.mode;
  return {
    dataset,
    // В «своей базе» встроенных заданий нет — режим заданий там невозможен.
    mode: dataset === "custom" && mode === "tasks" ? "free" : mode,
    setupSql: typeof obj?.setupSql === "string" ? obj.setupSql : d.setupSql,
    query: typeof obj?.query === "string" ? obj.query : d.query,
    taskIndex: Number.isFinite(obj?.taskIndex) ? Math.max(0, Math.round(Number(obj?.taskIndex))) : 0,
    customPrompt: typeof obj?.customPrompt === "string" ? obj.customPrompt : d.customPrompt,
    customSolution: typeof obj?.customSolution === "string" ? obj.customSolution : d.customSolution,
    status:
      obj?.status === "ok" || obj?.status === "fail" || obj?.status === "idle"
        ? obj.status
        : d.status,
    output: typeof obj?.output === "string" ? obj.output : d.output,
    solvedIds: Array.isArray(obj?.solvedIds)
      ? obj.solvedIds.filter((x): x is string => typeof x === "string").slice(0, 500)
      : [],
    showSchema: typeof obj?.showSchema === "boolean" ? obj.showSchema : d.showSchema,
  };
}

export function applySqlAction(state: SqlState, action: SqlAction): SqlState {
  switch (action.type) {
    case "setDataset": {
      const mode = action.dataset === "custom" && state.mode === "tasks" ? "free" : state.mode;
      // Новая база — старый результат и старый запрос к ней не относятся.
      return {
        ...state,
        dataset: action.dataset,
        mode,
        taskIndex: 0,
        query: SQL_QUERY_PLACEHOLDER,
        status: "idle",
        output: "",
      };
    }
    case "setMode":
      return { ...state, mode: action.mode, status: "idle", output: "" };
    case "setSetupSql":
      return { ...state, setupSql: clampStr(action.setupSql, SQL_SETUP_MAX) };
    case "setQuery":
      return { ...state, query: clampStr(action.query, SQL_TEXT_MAX) };
    case "setTaskIndex":
      return {
        ...state,
        taskIndex: Math.max(0, Math.round(action.taskIndex)),
        query: "",
        status: "idle",
        output: "",
      };
    case "setCustomPrompt":
      return { ...state, customPrompt: clampStr(action.customPrompt, 2_000) };
    case "setCustomSolution":
      return { ...state, customSolution: clampStr(action.customSolution, SQL_TEXT_MAX) };
    case "setResult": {
      const solved =
        action.solvedId && !state.solvedIds.includes(action.solvedId)
          ? [...state.solvedIds, action.solvedId].slice(-500)
          : state.solvedIds;
      return {
        ...state,
        status: action.status,
        output: clampStr(action.output, SQL_OUTPUT_MAX),
        solvedIds: solved,
      };
    }
    case "unsolve":
      // Снимаем отметку и гасим зелёную плашку — до следующего верного решения.
      return {
        ...state,
        solvedIds: state.solvedIds.filter((id) => id !== action.taskId),
        status: "idle",
      };
    case "resetSolved":
      return { ...state, solvedIds: [], status: "idle" };
    case "toggleSchema":
      return { ...state, showSchema: !state.showSchema };
    default:
      return state;
  }
}

export function sqlStateToPatch(state: SqlState): Record<string, unknown> {
  return {
    dataset: state.dataset,
    mode: state.mode,
    setupSql: state.setupSql,
    query: state.query,
    taskIndex: state.taskIndex,
    customPrompt: state.customPrompt,
    customSolution: state.customSolution,
    status: state.status,
    output: state.output,
    solvedIds: state.solvedIds,
    showSchema: state.showSchema,
  };
}

/** Скрипт подготовки базы для текущего состояния. */
export function setupSqlFor(state: SqlState): string {
  return datasetSetupSql(state.dataset, state.setupSql);
}

/** Условие и эталон текущего задания; null — режим свободного запроса. */
export function activeTask(
  state: SqlState
): { id: string; topic: string; prompt: string; solution: string; hint?: string } | null {
  if (state.mode === "free") return null;
  if (state.mode === "custom") {
    return {
      id: "custom",
      topic: "Своя задача",
      prompt: state.customPrompt,
      solution: state.customSolution,
    };
  }
  return taskAt(state.dataset, state.taskIndex);
}

/* ───────────────── сохранённый результат (виден обоим участникам) ───────────────── */

export type SqlOutputPayload = {
  table: SqlTableResult;
  verdict?: { ok: boolean; message: string };
};

export function encodeSqlOutput(payload: SqlOutputPayload): string {
  let json = JSON.stringify(payload);
  if (json.length <= SQL_OUTPUT_MAX) return json;
  // Слишком большая таблица — режем строки, чтобы результат всё же долетел до второго участника.
  const rows = payload.table.rows ?? [];
  let keep = Math.floor(rows.length / 2);
  while (keep > 0 && json.length > SQL_OUTPUT_MAX) {
    json = JSON.stringify({
      ...payload,
      table: { ...payload.table, rows: rows.slice(0, keep), truncated: true },
    });
    keep = Math.floor(keep / 2);
  }
  return json.length <= SQL_OUTPUT_MAX
    ? json
    : JSON.stringify({ table: { error: "Результат слишком большой для показа" } });
}

export function decodeSqlOutput(raw: string): SqlOutputPayload | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as SqlOutputPayload;
    if (!parsed || typeof parsed !== "object" || !parsed.table) return null;
    return parsed;
  } catch {
    return null;
  }
}
